import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Calendar, ExternalLink, Check, AlertOctagon } from "lucide-react";
import TurnstileField from "./TurnstileField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CAL_LINK = "https://cal.com/qortrace/30min";

const AUDIENCES = [
  "Individual / personal treasury",
  "Protocol / DeFi team",
  "Exchange / custodian",
  "Fund / asset manager",
  "Chain / L1 / L2 team",
  "Enterprise / regulator",
  "Other",
];

const ASSETS_BUCKETS = [
  "Less than $1M",
  "$1M – $10M",
  "$10M – $100M",
  "$100M – $1B",
  "More than $1B",
  "Prefer not to say",
];

const TIMELINES = [
  "Just exploring",
  "Within 1 – 3 months",
  "3 – 6 months",
  "6 – 12 months",
  "12+ months",
];

const BUDGETS = [
  "Less than $10k",
  "$10k – $50k",
  "$50k – $250k",
  "$250k+",
  "Open / to be discussed",
];

// Any consult source that indicates interest in a paid engagement — show qualifier fields.
function isPaidEngagementSource(src) {
  if (!src) return false;
  return (
    src.startsWith("pricing-service-") ||
    src === "pricing-tier-enterprise"
  );
}

/**
 * Resolve the attribution source for a consult submission.
 * Priority:
 *   1. ?ref= / ?source= / ?utm_source= query param   (channel attribution wins)
 *   2. Explicit prop                                 (page-context fallback)
 *   3. Pathname-derived                              (catch-all)
 *   4. "direct"
 *
 * Inverted from the original (prop-first) so a visitor arriving via a
 * tracked share link (e.g. `?ref=linkedin-d3-essay`) keeps that
 * attribution even when the local page passes its own static source.
 */
function resolveSource(propSource) {
  try {
    const sp = new URLSearchParams(window.location.search);
    const q = sp.get("ref") || sp.get("source") || sp.get("utm_source");
    if (q) return q.slice(0, 60);
  } catch (_) { /* SSR / no window */ }
  if (propSource) return propSource;
  try {
    const p = window.location.pathname;
    if (p === "/" || p === "") return "landing";
    return p.replace(/^\/+/, "").replace(/\/+$/, "").replace(/\//g, "-").slice(0, 60) || "direct";
  } catch (_) { return "direct"; }
}

export default function ConsultationModal({ open, onClose, source: propSource, prefillTopic = "" }) {
  const [form, setForm] = useState({
    name: "", email: "", company: "", role: "", audience: "", topic: "",
    assets_at_risk: "", timeline: "", budget_range: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [utmQuery, setUtmQuery] = useState("");
  const [tsToken, setTsToken] = useState("");

  // First-open topic prefill — only writes when the user hasn't typed
  // anything yet, so we never clobber an in-flight edit if the modal
  // is reopened mid-session. Lets share-link landings carry context
  // ("Came in via QTL-DEMO-001 LinkedIn share…") into the form.
  useEffect(() => {
    if (!open || !prefillTopic) return;
    setForm((f) => (f.topic ? f : { ...f, topic: prefillTopic }));
  }, [open, prefillTopic]);

  // Escape-to-close. Also locks page scroll while the modal is open
  // so the underlying page can't scroll behind the backdrop.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;
  const set = (k, v) => setForm({ ...form, [k]: v });
  const source = resolveSource(propSource);
  const showQualifiers = isPaidEngagementSource(source);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const referrer = (typeof document !== "undefined" && document.referrer) ? document.referrer : "";
      await axios.post(`${API}/consultations/request`, {
        ...form,
        booking_url: CAL_LINK,
        source,
        referrer,
        turnstile_token: tsToken,
      });
      // Pass attribution through to the Cal.com booking page so it shows up in Cal notifications too
      const q = new URLSearchParams({
        name: form.name || "",
        email: form.email || "",
        "metadata[source]": source,
      });
      setUtmQuery(q.toString());
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Could not submit request");
    } finally { setLoading(false); }
  };

  return (
    <div
      data-testid="consultation-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consultation-modal-title"
      style={{
        position: "fixed", inset: 0,
        background: "rgba(4,9,20,0.94)",
        /* Iter255 — opaque backdrop */
        zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div className="qt-card w-full max-w-2xl qt-grain" style={{ maxHeight: "92vh", overflowY: "auto" }}>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Calendar size={16} style={{ color: "var(--qt-cyan)" }} aria-hidden="true" />
              <div className="qt-label">FREE · 30 MIN</div>
            </div>
            <h2 id="consultation-modal-title" className="qt-h2 text-2xl mt-2">Schedule a QorTrace consultation</h2>
            <p className="text-sm mt-2" style={{ color: "var(--qt-text-2)" }}>
              A QorTrace strategist will walk you through your quantum exposure, migration options,
              and whether a paid audit or migration engagement is the right next step.
            </p>
          </div>
          <button onClick={onClose} className="qt-btn qt-btn-ghost" data-testid="consult-close" aria-label="Close consultation modal" style={{ padding: "4px 8px" }}>
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        {submitted ? (
          <div className="mt-6">
            <div className="qt-card" style={{ background: "rgba(0,210,106,0.06)", border: "1px solid rgba(0,210,106,0.4)" }}>
              <div className="flex items-center gap-2">
                <Check size={16} style={{ color: "#1FDD83" }} />
                <div className="qt-label" style={{ color: "#1FDD83" }}>RECEIVED</div>
              </div>
              <p className="mt-2 text-sm" style={{ color: "#fff" }}>
                Thanks, {form.name || "friend"}. Your request is logged. Pick a time that works for you below
                — or we'll reach out within one business day if you prefer.
              </p>
            </div>
            <div className="mt-5 flex gap-3 flex-wrap">
              <a href={`${CAL_LINK}?${utmQuery}`} target="_blank" rel="noreferrer" data-testid="open-cal-link"
                 className="qt-btn qt-btn-primary">
                Pick a time on Cal.com <ExternalLink size={14} />
              </a>
              <button onClick={onClose} className="qt-btn qt-btn-secondary">Done</button>
            </div>
            <div className="mt-4">
              <iframe
                src={`${CAL_LINK}?embed=true${utmQuery ? `&${utmQuery}` : ""}`}
                title="Book a consultation"
                width="100%" height="620"
                frameBorder="0"
                style={{ border: "1px solid var(--qt-border)", borderRadius: 6, background: "#fff" }}
                data-testid="cal-iframe"
              />
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="consult-form">
            <div>
              <div className="qt-label mb-2">YOUR NAME</div>
              <input className="qt-input" required value={form.name} data-testid="consult-name"
                     onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <div className="qt-label mb-2">WORK EMAIL</div>
              <input type="email" className="qt-input" required value={form.email} data-testid="consult-email"
                     onChange={(e) => set("email", e.target.value)} />
            </div>
            <div>
              <div className="qt-label mb-2">COMPANY / PROTOCOL</div>
              <input className="qt-input" value={form.company} data-testid="consult-company"
                     onChange={(e) => set("company", e.target.value)} />
            </div>
            <div>
              <div className="qt-label mb-2">YOUR ROLE</div>
              <input className="qt-input" placeholder="CISO · Protocol Eng · Head of Custody…"
                     value={form.role} data-testid="consult-role"
                     onChange={(e) => set("role", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <div className="qt-label mb-2">AUDIENCE</div>
              <select className="qt-select w-full" value={form.audience} data-testid="consult-audience"
                      onChange={(e) => set("audience", e.target.value)}>
                <option value="">— Select —</option>
                {AUDIENCES.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <div className="qt-label mb-2">WHAT WOULD YOU LIKE TO DISCUSS?</div>
              <textarea rows={4} className="qt-input" data-testid="consult-topic"
                        placeholder="E.g. 'We hold ~$80M across BTC + ETH treasuries; need a migration roadmap.'"
                        value={form.topic} onChange={(e) => set("topic", e.target.value)} />
            </div>
            {showQualifiers && (
              <div className="md:col-span-2 qt-card" style={{ background: "rgba(0,224,255,0.04)", border: "1px solid rgba(0,224,255,0.25)" }}>
                <div className="qt-label mb-3" style={{ color: "var(--qt-cyan)" }}>QUALIFYING QUESTIONS · PAID ENGAGEMENT</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <div className="qt-label mb-2">ASSETS UNDER CRYPTOGRAPHIC RISK</div>
                    <select className="qt-select w-full" data-testid="consult-assets"
                            value={form.assets_at_risk} onChange={(e) => set("assets_at_risk", e.target.value)}>
                      <option value="">— Select —</option>
                      {ASSETS_BUCKETS.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div>
                    <div className="qt-label mb-2">TIMELINE</div>
                    <select className="qt-select w-full" data-testid="consult-timeline"
                            value={form.timeline} onChange={(e) => set("timeline", e.target.value)}>
                      <option value="">— Select —</option>
                      {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <div className="qt-label mb-2">BUDGET RANGE (USD)</div>
                    <select className="qt-select w-full" data-testid="consult-budget"
                            value={form.budget_range} onChange={(e) => set("budget_range", e.target.value)}>
                      <option value="">— Select —</option>
                      {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}
            {error && (
              <div className="md:col-span-2 px-3 py-2 rounded text-sm"
                   style={{ background: "rgba(255,42,42,0.08)", border: "1px solid rgba(255,42,42,0.4)", color: "#FF6E6E" }}>
                <AlertOctagon size={14} className="inline -mt-1 mr-2" />{error}
              </div>
            )}
            <div className="md:col-span-2">
              <TurnstileField action="consult" onToken={setTsToken} />
            </div>
            <div className="md:col-span-2 flex items-center justify-between gap-3 flex-wrap">
              <div className="qt-mono text-xs" style={{ color: "var(--qt-text-3)" }}>
                By submitting, you agree to be contacted by QorTrace.
              </div>
              <button disabled={loading} data-testid="consult-submit" className="qt-btn qt-btn-primary">
                {loading ? "SUBMITTING…" : "Submit & pick a time"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
